import type { EventCondition } from "@packages/commons";
import React, { createContext, useContext, useEffect, useState } from 'react';
import Form from '@imminently/decisively-ui/Form';
import { getSettings, updateSettings } from './settings.api';
import EmptyStatus from "@imminently/decisively-ui/EmptyStatus";
import { produce } from 'immer';
import { getActiveTenancy } from '@/features/tenancy';

export interface Email {
  id: string;
  name: string;
  template: File | string | null;
  templateFilename?: string;
  templateSize?: number;
}

export interface EventConnector {
  id: string;
  type: "slack" | "webhook" | "email" | "teams" | "clickup";
  name: string;
  webhookUrl?: string;
  emailTo?: string[];
  emailToByEvent?: Record<string, string[]>;
  clickUpToken?: string;
  clickUpListId?: string;
  slackBotToken?: string;
  slackChannelId?: string;
  subscribedEvents?: string[];
  conditions?: EventCondition | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SettingsData {
  emails: Email[];
  connectors: EventConnector[];
  general: any;
  features: any;
  orgModels: {
    implyApprovers: boolean;
    recomputeEnabled: boolean;
  };
}

interface SettingsContextValue {
  form: any;
  isLoading: boolean;
  isSaving: boolean;
  saveSettings: () => Promise<void>;
  resetSettings: () => void;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

/**
 * DEV NOTE
 * Ensure you put new settings into the default, as the form reset relies on it
 * to avoid undefined values causing isDirty to always be true.
 */
const DEFAULT_SETTINGS: SettingsData = {
  emails: [],
  connectors: [],
  general: {
    defaultEmail: undefined,
  },
  features: {},
  orgModels: {
    implyApprovers: false,
    recomputeEnabled: true,
  },
};

/**
 * Pre-processes settings data before saving.
 * Ensures default values are set where appropriate.
 */
const preSubmitSettings = (data: SettingsData): SettingsData => {
  return produce(data, (draft) => {
    // Ensure general.defaultEmail is set, defaulting to first email if available
    if (!draft.general.defaultEmail && draft.emails.length > 0) {
      const firstEmail = draft.emails[0];
      if (firstEmail.template && typeof firstEmail.template === 'string' && firstEmail.template.startsWith('storage://')) {
        draft.general.defaultEmail = firstEmail.template;
      }
    }

    // Ensure orgModels settings are always present and boolean.
    draft.orgModels = {
      implyApprovers: draft.orgModels?.implyApprovers === true,
      recomputeEnabled: draft.orgModels?.recomputeEnabled !== false,
    };
  });
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenancyId = getActiveTenancy();

  if(!tenancyId) {
    return (
      <EmptyStatus>
        <EmptyStatus.Title>No Tenancy Selected</EmptyStatus.Title>
        <EmptyStatus.Content>
          Please select a tenancy to manage its settings.
        </EmptyStatus.Content>
      </EmptyStatus>
    )
  }

  return (
    <Form<SettingsData> defaultValues={DEFAULT_SETTINGS} onSubmit={async () => undefined} className={"w-full h-full"}>
      {(form) => (
        <SettingsProviderInner form={form} tenancyId={tenancyId}>
          {children}
        </SettingsProviderInner>
      )}
    </Form>
  );
};

const SettingsProviderInner: React.FC<{
  children: React.ReactNode;
  tenancyId: string;
  form: any;
}> = ({ children, tenancyId, form }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<SettingsData | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!tenancyId) return;

      try {
        setIsLoading(true);
        const res = await getSettings.submit({ params: { id: tenancyId } });
        const data = res.data;
        const settingsData = {
          emails: data.emails || [],
          connectors: data.connectors || [],
          general: data.general || {},
          features: data.features || {},
          orgModels: {
            ...DEFAULT_SETTINGS.orgModels,
            ...(data.orgModels || {}),
          },
        };
        setLastSavedData(settingsData);
        form.reset(settingsData);
      } catch (error: any) {
        if (error.message.includes("missing")) {
          return;
        }
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [tenancyId, form]);

  const saveSettings = async () => {
    if (!tenancyId) return;

    try {
      setIsSaving(true);
      const data = form.getValues();
      const processedData = preSubmitSettings(data);
      const res = await updateSettings.submit({ params: { id: tenancyId }, body: processedData });
      const savedSettingsData = {
        ...DEFAULT_SETTINGS,
        ...res.data,
        general: {
          ...DEFAULT_SETTINGS.general,
          ...(res.data.general || {}),
        },
        orgModels: {
          ...DEFAULT_SETTINGS.orgModels,
          ...(res.data.orgModels || {}),
        },
      };
      setLastSavedData(savedSettingsData);
      form.reset(savedSettingsData);
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const resetSettings = () => {
    if (!lastSavedData) return;

    const mergedData = {
      ...DEFAULT_SETTINGS,
      ...lastSavedData,
      general: {
        ...DEFAULT_SETTINGS.general,
        ...lastSavedData.general,
      },
      orgModels: {
        ...DEFAULT_SETTINGS.orgModels,
        ...lastSavedData.orgModels,
      },
    };
    form.reset(mergedData);
  };

  return (
    <SettingsContext.Provider
      value={{
        form,
        isLoading,
        isSaving,
        saveSettings,
        resetSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};
